import { useQueryClient, type QueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';

import { useAuthedApi } from '@/features/wallet';
import { recheckSpeedJob, type SpeedJobResponse } from '@/lib/api/speed';
import { hyperEvmPublicClient } from '@/lib/hyperEvmPublicClient';
import { queryKeys } from '@/lib/query';

import {
    nextObservationDelayMs,
    observeHyperEvmJob,
    shouldRequestJobRecheck,
} from '../jobObservation';

const OBSERVE_INTERVAL_MS = 5_000;
// the backend abandons an operation after 60 minutes, and a Cronos -> HyperEVM deposit can take
// well over five (a user who signs slowly, a slow bridge). This used to be five minutes, so a job
// signed eleven minutes in was never rechecked again after its hashes were posted, and the
// backend only advances an actionRequired job on a recheck: the deposit landed while the job sat
// on "Wallet signature required" until the fence parked it. Rechecks back off on their own
// (jobObservation), so a long window costs a request every few minutes, not every five seconds.
const OPERATION_TIMEOUT_MS = 60 * 60_000;

function isRecheckable(job: SpeedJobResponse, now: number): boolean {
    return (
        (job.status === 'bridging' || (
            job.status === 'actionRequired' &&
            job.kind === 'deposit' &&
            job.step === 'awaitingWalletSignature' &&
            job.transferId !== null &&
            job.transferId !== undefined &&
            job.response?.route === 'metamask'
        )) &&
        now - new Date(job.createdAt).getTime() < OPERATION_TIMEOUT_MS
    );
}

// ---------------------------------------------------------------------------
// One observer per tab.
//
// This used to be per-hook-instance state with its own setInterval, and the hook is reached from
// about nine places (useSnappyWalletState -> useAgentReactivation -> useCustodialJobs, plus
// SpeedTradeRail, MobileControlPanel, the banners and the panel container). React Query dedupes the
// jobs QUERY because it is keyed; a bare effect and timer are not deduped by anything, so every
// mount ran its own loop and fired its own recheck at the same instant — ten requests for one job,
// of which the rate limiter accepted exactly one.
//
// The loop is module state now and refcounted by the hook, so mounting it a tenth time is free.
// ---------------------------------------------------------------------------

const fingerprints = new Map<string, string>();
const lastRechecks = new Map<string, number>();
const unchangedRechecks = new Map<string, number>();
const unchangedSnapshots = new Map<string, number>();
const nextObservations = new Map<string, number>();

let subscribers = 0;
let timer: ReturnType<typeof setInterval> | null = null;
let running = false;
let forceNext = true;
let context: {
    api: NonNullable<ReturnType<typeof useAuthedApi>>;
    queryClient: QueryClient;
} | null = null;

function currentJobs(queryClient: QueryClient): SpeedJobResponse[] {
    // read from the shared cache rather than a prop: it is the same data every caller sees, and
    // taking it from whichever component happened to render last is how the old version ended up
    // observing an empty list when one caller was disabled.
    return (
        queryClient.getQueryData<{ jobs: SpeedJobResponse[] }>(
            queryKeys.speed.jobs(),
        )?.jobs ?? []
    );
}

function replaceJob(queryClient: QueryClient, updated: SpeedJobResponse) {
    queryClient.setQueryData<{ jobs: SpeedJobResponse[] }>(
        queryKeys.speed.jobs(),
        (current) =>
            current
                ? {
                    jobs: current.jobs.map((job) =>
                        job.jobId === updated.jobId ? updated : job,
                    ),
                }
                : current,
    );
}

async function run(): Promise<void> {
    if (
        !context ||
        running ||
        document.visibilityState !== 'visible' ||
        !navigator.onLine
    ) {
        return;
    }
    const { api, queryClient } = context;
    running = true;
    const force = forceNext;
    forceNext = false;
    try {
        const now = Date.now();
        const active = currentJobs(queryClient).filter((job) =>
            isRecheckable(job, now),
        );
        await Promise.allSettled(
            active.map(async (job) => {
                const previous = fingerprints.get(job.jobId);
                const requestRecheck = async () => {
                    try {
                        replaceJob(queryClient, await recheckSpeedJob(api, job.jobId));
                    } catch (error) {
                        const status = (error as { response?: Response })
                            ?.response?.status;
                        if (status !== 429) throw error;
                        // With a single observer per tab and a nudge-only endpoint this should not
                        // happen, so it is worth seeing rather than swallowing: a 429 now means
                        // something really is over-calling, which is the signal the old
                        // one-per-five-seconds limit drowned out.
                        console.warn(
                            '[SpeedJobObserver] recheck rate limited — unexpected with one observer per tab',
                            { jobId: job.jobId },
                        );
                    } finally {
                        lastRechecks.set(job.jobId, Date.now());
                    }
                };
                if (force || previous === undefined) {
                    await requestRecheck();
                }
                if (!force && now < (nextObservations.get(job.jobId) ?? 0)) {
                    return;
                }
                const observation = await observeHyperEvmJob(
                    job,
                    hyperEvmPublicClient,
                );
                const observedAt = Date.now();
                fingerprints.set(job.jobId, observation.fingerprint);
                const unchanged = previous === observation.fingerprint
                    ? (unchangedSnapshots.get(job.jobId) ?? 0) + 1
                    : 0;
                unchangedSnapshots.set(job.jobId, unchanged);
                nextObservations.set(
                    job.jobId,
                    observedAt + nextObservationDelayMs(unchanged),
                );
                if (force || previous === undefined) return;
                if (previous !== observation.fingerprint) {
                    unchangedRechecks.set(job.jobId, 0);
                }
                if (
                    !shouldRequestJobRecheck({
                        previousFingerprint: previous,
                        fingerprint: observation.fingerprint,
                        force,
                        hasObservableHashes: observation.hasObservableHashes,
                        lastRecheckAt: lastRechecks.get(job.jobId),
                        now: observedAt,
                        unchangedRechecks: unchangedRechecks.get(job.jobId),
                    })
                ) {
                    return;
                }
                await requestRecheck();
                if (!observation.hasObservableHashes) {
                    unchangedRechecks.set(
                        job.jobId,
                        (unchangedRechecks.get(job.jobId) ?? 0) + 1,
                    );
                }
            }),
        );

        const activeIds = new Set(active.map((job) => job.jobId));
        for (const jobId of [...fingerprints.keys()]) {
            if (!activeIds.has(jobId)) {
                fingerprints.delete(jobId);
                lastRechecks.delete(jobId);
                unchangedRechecks.delete(jobId);
                unchangedSnapshots.delete(jobId);
                nextObservations.delete(jobId);
            }
        }
    } finally {
        // `running` is the re-entrancy guard itself, not state derived from the awaited work. JS is
        // single-threaded, so the only interleaving possible is another run() starting between
        // ticks — exactly what this flag prevents. Clearing it is the release half of that guard.
        // eslint-disable-next-line require-atomic-updates
        running = false;
    }
}

function requestForcedSync() {
    if (document.visibilityState === 'visible' && navigator.onLine) {
        forceNext = true;
        void run();
    }
}

function startObserver() {
    if (timer !== null) return;
    document.addEventListener('visibilitychange', requestForcedSync);
    window.addEventListener('focus', requestForcedSync);
    window.addEventListener('online', requestForcedSync);
    forceNext = true;
    void run();
    timer = setInterval(() => void run(), OBSERVE_INTERVAL_MS);
}

function stopObserver() {
    if (timer === null) return;
    clearInterval(timer);
    timer = null;
    document.removeEventListener('visibilitychange', requestForcedSync);
    window.removeEventListener('focus', requestForcedSync);
    window.removeEventListener('online', requestForcedSync);
    fingerprints.clear();
    lastRechecks.clear();
    unchangedRechecks.clear();
    unchangedSnapshots.clear();
    nextObservations.clear();
}

/** test-only: drops the singleton so a suite starts from a known state. */
export function resetSpeedJobObserver(): void {
    subscribers = 0;
    context = null;
    running = false;
    stopObserver();
}

/**
 * Observes in-flight deposit/withdraw jobs and nudges the backend to advance them.
 *
 * Safe to call from as many components as need job state — the underlying loop runs once per tab.
 */
export function useSpeedJobObserver(enabled = true): void {
    const authedApi = useAuthedApi();
    const queryClient = useQueryClient();

    useEffect(() => {
        if (!enabled || !authedApi) return;
        context = { api: authedApi, queryClient };
        subscribers += 1;
        startObserver();
        return () => {
            subscribers -= 1;
            if (subscribers <= 0) {
                subscribers = 0;
                context = null;
                stopObserver();
            }
        };
    }, [enabled, authedApi, queryClient]);
}
